import { useEffect, useMemo, useRef, useState } from "react";
import { Save, Sparkles, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  buildLUT,
  buildLayerIndexLUT,
  getCombinedMaterialDescriptors,
  renderColorMap,
  imageDataToCanvas,
} from "@/lib/heightMapUtils";
import { ZoomCanvas } from "@/components/height-map/ZoomCanvas";
import { DualRangeSlider } from "@/components/height-map/DualRangeSlider";

const PATTERNS = [
  { id: "noise", label: "Noise" },
  { id: "checker", label: "Checker" },
  { id: "stripes", label: "Stripes" },
  { id: "dots", label: "Dots" },
];

export const AdvancedMode = ({
  sourceImageData,
  layers,
  mode,
  allSolid,
  bigViewports,
  paintOverlay,
  showReference,
  referenceOpacity = 0.4,
  customPatterns = [],
  onSaveCombinedLayer,
  isImprovedUI = false,
  onRenderControls,
  roundedMapPreview = false,
}) => {
  const canvasRef = useRef(null);
  const [combineName, setCombineName] = useState("Mixed Material");
  const [selectedIds, setSelectedIds] = useState(() => layers.slice(0, 2).map((layer) => layer.id));
  const [weights, setWeights] = useState({});
  const [pattern, setPattern] = useState("noise");
  const [scale, setScale] = useState(6);
  const [intensity, setIntensity] = useState(0.5);
  const [rotation, setRotation] = useState(0);
  const [range, setRange] = useState({ min: 60, max: 140 });
  const [destination, setDestination] = useState("new");
  const [existingLayerId, setExistingLayerId] = useState(() => layers[0]?.id ?? "");
  const [outputMaterialId, setOutputMaterialId] = useState(() => layers[0]?.id ?? "");

  useEffect(() => {
    const layerIds = new Set(layers.map((layer) => layer.id));
    setSelectedIds((previous) => {
      const valid = previous.filter((id) => layerIds.has(id));
      return valid.length ? valid : layers.slice(0, 2).map((layer) => layer.id);
    });
    setExistingLayerId((previous) =>
      layerIds.has(previous) ? previous : layers[0]?.id ?? "",
    );
    setOutputMaterialId((previous) =>
      layerIds.has(previous) ? previous : layers[0]?.id ?? "",
    );
  }, [layers]);

  const selected = useMemo(
    () => selectedIds.map((id) => layers.find((layer) => layer.id === id)).filter(Boolean),
    [selectedIds, layers],
  );
  const allPatterns = useMemo(
    () => [
      ...PATTERNS,
      ...customPatterns.map((p) => ({ id: `custom:${p.id}`, label: p.name, custom: p })),
    ],
    [customPatterns],
  );
  useEffect(() => {
    if (!allPatterns.some((pp) => pp.id === pattern)) setPattern("noise");
  }, [allPatterns, pattern]);
  const materials = useMemo(
    () => selected.map((layer) => ({ color: layer.color, weight: weights[layer.id] ?? 1 })),
    [selected, weights],
  );
  const destinationLayer = layers.find((layer) => layer.id === existingLayerId) ?? layers[0];
  const outputMaterial = layers.find((layer) => layer.id === outputMaterialId) ?? selected[0] ?? layers[0];
  const draftCombine = useMemo(
    () => {
      const sel = allPatterns.find((pp) => pp.id === pattern);
      const spec = sel?.custom
        ? {
            pattern: "custom",
            customCells: sel.custom.cells,
            customWidth: sel.custom.width ?? sel.custom.size,
            customHeight: sel.custom.height ?? sel.custom.size,
          }
        : { pattern };
      return { materials, ...spec, scale, intensity, rotation };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allPatterns, materials, pattern, scale, intensity, rotation],
  );

  // Build the exact same virtual layer arrangement that will be saved. The
  // preview therefore matches Elevation, Manual Paint, and 3D after saving.
  const previewLayers = useMemo(() => {
    if (selected.length < 2 || !outputMaterial) return layers;
    if (destination === "existing" && destinationLayer) {
      return layers.map((layer) =>
        layer.id === destinationLayer.id
          ? {
              ...layer,
              min: range.min,
              max: range.max,
              solid: true,
              combine: draftCombine,
            }
          : layer,
      );
    }
    return [
      {
        id: "advanced-preview-layer",
        name: combineName || "Mixed Material",
        color: outputMaterial.color,
        min: range.min,
        max: range.max,
        solid: true,
        combine: draftCombine,
      },
      ...layers,
    ];
  }, [combineName, destination, destinationLayer, draftCombine, layers, outputMaterial, range, selected.length]);

  const previewLut = useMemo(
    () => buildLUT(previewLayers, mode, allSolid),
    [previewLayers, mode, allSolid],
  );
  const previewOwners = useMemo(() => buildLayerIndexLUT(previewLayers), [previewLayers]);
  const previewCombines = useMemo(
    () => getCombinedMaterialDescriptors(previewLayers),
    [previewLayers],
  );

  useEffect(() => {
    if (!sourceImageData || !canvasRef.current) return;
    const canvas = canvasRef.current;
    canvas.width = sourceImageData.width;
    canvas.height = sourceImageData.height;
    const ctx = canvas.getContext("2d");
    ctx.putImageData(renderColorMap(sourceImageData, previewLut, previewCombines, previewOwners), 0, 0);
    // Locked Exact-Paint overlay shows here too, on top of the combined preview.
    if (
      paintOverlay &&
      paintOverlay.width === sourceImageData.width &&
      paintOverlay.height === sourceImageData.height
    ) {
      ctx.drawImage(imageDataToCanvas(paintOverlay), 0, 0);
    }
    if (showReference) {
      ctx.save();
      ctx.globalAlpha = referenceOpacity;
      ctx.drawImage(imageDataToCanvas(sourceImageData), 0, 0);
      ctx.restore();
    }
  }, [sourceImageData, previewLut, previewCombines, previewOwners, paintOverlay, showReference, referenceOpacity]);

  const toggleLayer = (id) => {
    setSelectedIds((previous) =>
      previous.includes(id) ? previous.filter((selectedId) => selectedId !== id) : [...previous, id],
    );
  };

  const selectExistingDestination = (id) => {
    setExistingLayerId(id);
    const layer = layers.find((item) => item.id === id);
    if (layer) {
      setRange({ min: layer.min, max: layer.max });
      setCombineName(layer.name);
    }
  };

  const saveCombinedLayer = () => {
    if (selected.length < 2) {
      toast.error("Pick at least two materials to combine.");
      return;
    }
    if (destination === "existing" && !destinationLayer) {
      toast.error("Choose an existing height layer.");
      return;
    }
    if (!outputMaterial) {
      toast.error("Choose a layer colour for the new height layer.");
      return;
    }

    const usingExistingLayer = destination === "existing";
    onSaveCombinedLayer(
      {
        name: usingExistingLayer ? destinationLayer.name : combineName || "Mixed Material",
        // New combined layers are assigned an existing material's exact hex.
        // Existing layers keep their own exact hex. No averaged swatch is made.
        color: usingExistingLayer ? destinationLayer.color : outputMaterial.color,
        min: range.min,
        max: range.max,
        combine: draftCombine,
      },
      usingExistingLayer ? destinationLayer.id : null,
    );
    toast.success(usingExistingLayer ? `Updated "${destinationLayer.name}".` : `Saved "${combineName || "Mixed Material"}" as a layer.`);
  };

  const controlsMarkup = (
    <div className="space-y-4 text-left">
      <div>
        <div className="text-zinc-100 font-semibold flex items-center gap-2 mb-1.5 text-sm">
          <Sparkles className="w-4 h-4 text-rose-400" /> Combine Materials
        </div>
        <div className="text-[11px] text-zinc-500 leading-relaxed">
          Pattern pixels use the selected layer colours exactly. This tool never blends colours into a new code.
        </div>
      </div>

      <div className="space-y-1.5">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
          Save combined material to
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          <ChoiceButton active={destination === "new"} onClick={() => setDestination("new")} testId="combine-destination-new">
            New layer
          </ChoiceButton>
          <ChoiceButton active={destination === "existing"} onClick={() => setDestination("existing")} testId="combine-destination-existing">
            Existing layer
          </ChoiceButton>
        </div>
      </div>

      {destination === "new" ? (
        <>
          <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Layer name</div>
            <Input
              data-testid="combine-name"
              value={combineName}
              onChange={(event) => setCombineName(event.target.value)}
              className="bg-[#131418] border-zinc-800 text-zinc-100 h-8 text-xs"
            />
          </div>
          <LayerSelect
            label="Base colour for new layer"
            hint="Pick which existing material's exact colour to use."
            testId="combine-output-material"
            layers={layers}
            value={outputMaterial?.id ?? ""}
            onChange={setOutputMaterialId}
          />
        </>
      ) : (
        <LayerSelect
          label="Existing layer to replace"
          hint="Choose which height layer this combined pattern is written into."
          testId="combine-existing-layer"
          layers={layers}
          value={destinationLayer?.id ?? ""}
          onChange={selectExistingDestination}
        />
      )}

      <div className="space-y-1.5">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
          Materials ({selected.length})
        </div>
        <div className="max-h-36 overflow-y-auto scroll-thin pr-1 space-y-1">
          {layers.map((layer) => {
            const checked = selectedIds.includes(layer.id);
            return (
              <label
                key={layer.id}
                data-testid={`combine-layer-${layer.name}`}
                className={`flex items-center gap-2 rounded-md px-2 py-1 cursor-pointer transition text-xs ${
                  checked
                    ? "bg-rose-500/10 border border-rose-500/30"
                    : "hover:bg-zinc-800/40 border border-transparent"
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleLayer(layer.id)}
                  className="accent-rose-500"
                />
                <span className="w-3.5 h-3.5 rounded-sm border border-zinc-700" style={{ backgroundColor: layer.color }} />
                <span className="text-xs text-zinc-100 flex-1 truncate">{layer.name}</span>
                {checked && (
                  <input
                    type="range"
                    min={0.1}
                    max={3}
                    step={0.1}
                    value={weights[layer.id] ?? 1}
                    onChange={(event) =>
                      setWeights((previous) => ({ ...previous, [layer.id]: Number(event.target.value) }))
                    }
                    className="w-12 accent-rose-500"
                    title="Weight"
                  />
                )}
              </label>
            );
          })}
        </div>
      </div>

      <div className="space-y-1.5">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Pattern</div>
        <div className="grid grid-cols-4 gap-1">
          {allPatterns.map((item) => (
            <button
              key={item.id}
              data-testid={`pattern-${item.id}`}
              onClick={() => setPattern(item.id)}
              className={`text-[11px] py-1 rounded-md transition truncate px-1 ${
                pattern === item.id
                  ? "brand-gradient text-white font-semibold"
                  : "bg-zinc-800/60 text-zinc-300 hover:bg-zinc-700/60"
              }`}
              title={item.label}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <SliderRow label="Pattern scale" value={scale} min={1} max={60} step={1} onChange={setScale} testId="pattern-scale" />
      <SliderRow label="Rotation" value={rotation} min={0} max={360} step={1} onChange={setRotation} format={(value) => `${value}°`} testId="pattern-rotation" />
      <SliderRow label="Density (intensity)" value={intensity} min={0} max={1} step={0.02} onChange={setIntensity} format={(value) => `${Math.round(value * 100)}%`} testId="pattern-intensity" />

      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
          <span>Height band</span>
          <span className="mono text-zinc-400 normal-case">{range.min} · {range.max}</span>
        </div>
        <DualRangeSlider
          testId="combine-range"
          min={0}
          max={255}
          low={range.min}
          high={range.max}
          onChange={(min, max) => setRange({ min, max })}
        />
      </div>

      <Button
        data-testid="save-combined-layer"
        onClick={saveCombinedLayer}
        size="sm"
        className="w-full brand-gradient text-white font-semibold text-xs py-2 shadow-md shadow-rose-500/20"
      >
        <Save className="w-3.5 h-3.5 mr-1.5" /> {destination === "existing" ? "Update selected layer" : "Save as new layer"}
      </Button>
    </div>
  );

  useEffect(() => {
    if (isImprovedUI && onRenderControls) {
      onRenderControls(controlsMarkup);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isImprovedUI,
    combineName,
    selectedIds,
    weights,
    pattern,
    scale,
    intensity,
    rotation,
    range.min,
    range.max,
    destination,
    existingLayerId,
    outputMaterialId,
    layers,
    customPatterns,
  ]);

  const viewportClass = bigViewports ? "min-h-[680px]" : "min-h-[520px]";
  const canvasClass = bigViewports ? "max-h-[720px]" : "max-h-[560px]";

  if (isImprovedUI) {
    return (
      <div className="absolute inset-0">
        <ZoomCanvas testId="advanced-zoom" className="w-full h-full">
          <canvas
            data-testid="advanced-canvas"
            ref={canvasRef}
            className={`max-w-full max-h-full object-contain ${roundedMapPreview ? "rounded-xl" : ""}`}
            style={{ imageRendering: "pixelated" }}
          />
        </ZoomCanvas>
      </div>
    );
  }

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_400px]">
      <div className="rounded-2xl border border-zinc-800/80 bg-[#101116] overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-800/60">
          <div className="flex items-center gap-2 text-xs text-zinc-400">
            <Sparkles className="w-3.5 h-3.5 text-rose-400" />
            <span className="uppercase tracking-wider">Combined Preview</span>
          </div>
          <span className="text-[11px] text-zinc-500 mono">
            {sourceImageData ? `${sourceImageData.width}×${sourceImageData.height}` : "no image"}
          </span>
        </div>
        <ZoomCanvas testId="advanced-zoom" className={viewportClass}>
          <canvas
            data-testid="advanced-canvas"
            ref={canvasRef}
            className={`max-w-full ${canvasClass} object-contain ${roundedMapPreview ? "rounded-xl" : ""}`}
            style={{ imageRendering: "pixelated" }}
          />
        </ZoomCanvas>
      </div>

      <div className="rounded-2xl border border-zinc-800/80 bg-[#101116] p-5 space-y-5">
        <div>
          <div className="text-zinc-100 font-semibold flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-rose-400" /> Combine Materials
          </div>
          <div className="text-xs text-zinc-500 leading-relaxed">
            Pattern pixels use the selected layer colours exactly. This tool never blends colours into a new code.
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
            Save combined material to
          </div>
          <div className="grid grid-cols-2 gap-2">
            <ChoiceButton active={destination === "new"} onClick={() => setDestination("new")} testId="combine-destination-new">
              New layer
            </ChoiceButton>
            <ChoiceButton active={destination === "existing"} onClick={() => setDestination("existing")} testId="combine-destination-existing">
              Existing layer
            </ChoiceButton>
          </div>
        </div>

        {destination === "new" ? (
          <>
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Layer name</div>
              <Input
                data-testid="combine-name"
                value={combineName}
                onChange={(event) => setCombineName(event.target.value)}
                className="bg-[#131418] border-zinc-800 text-zinc-100"
              />
            </div>
            <LayerSelect
              label="Base colour for the new layer"
              hint="A combined layer still needs one on-palette colour for its swatch and for any area the pattern doesn't cover. Pick which existing material's exact colour to use — nothing is blended or invented."
              testId="combine-output-material"
              layers={layers}
              value={outputMaterial?.id ?? ""}
              onChange={setOutputMaterialId}
            />
          </>
        ) : (
          <LayerSelect
            label="Existing layer to replace"
            hint="Choose which of your current height layers this combined pattern is written into. The layer keeps its own name and colour — only its height band and pattern get updated."
            testId="combine-existing-layer"
            layers={layers}
            value={destinationLayer?.id ?? ""}
            onChange={selectExistingDestination}
          />
        )}

        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
            Materials ({selected.length})
          </div>
          <div className="max-h-40 overflow-y-auto scroll-thin pr-1 space-y-1">
            {layers.map((layer) => {
              const checked = selectedIds.includes(layer.id);
              return (
                <label
                  key={layer.id}
                  data-testid={`combine-layer-${layer.name}`}
                  className={`flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer transition ${
                    checked
                      ? "bg-rose-500/10 border border-rose-500/30"
                      : "hover:bg-zinc-800/40 border border-transparent"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleLayer(layer.id)}
                    className="accent-rose-500"
                  />
                  <span className="w-4 h-4 rounded-sm border border-zinc-700" style={{ backgroundColor: layer.color }} />
                  <span className="text-sm text-zinc-100 flex-1">{layer.name}</span>
                  <span className="text-[10px] mono text-zinc-500">{layer.color.toUpperCase()}</span>
                  {checked && (
                    <input
                      type="range"
                      min={0.1}
                      max={3}
                      step={0.1}
                      value={weights[layer.id] ?? 1}
                      onChange={(event) =>
                        setWeights((previous) => ({ ...previous, [layer.id]: Number(event.target.value) }))
                      }
                      className="w-14 accent-rose-500"
                      title="Weight"
                    />
                  )}
                </label>
              );
            })}
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Pattern</div>
          <div className="grid grid-cols-4 gap-1">
            {allPatterns.map((item) => (
              <button
                key={item.id}
                data-testid={`pattern-${item.id}`}
                onClick={() => setPattern(item.id)}
                className={`text-xs py-1.5 rounded-md transition truncate px-1 ${
                  pattern === item.id
                    ? "brand-gradient text-white"
                    : "bg-zinc-800/60 text-zinc-300 hover:bg-zinc-700/60"
                }`}
                title={item.label}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <SliderRow label="Pattern scale" value={scale} min={1} max={60} step={1} onChange={setScale} testId="pattern-scale" />
        <SliderRow label="Rotation" value={rotation} min={0} max={360} step={1} onChange={setRotation} format={(value) => `${value}°`} testId="pattern-rotation" />
        <SliderRow label="Density (intensity)" value={intensity} min={0} max={1} step={0.02} onChange={setIntensity} format={(value) => `${Math.round(value * 100)}%`} testId="pattern-intensity" />

        <div className="space-y-2">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
            <span>Height band</span>
            <span className="mono text-zinc-400 normal-case">{range.min} · {range.max}</span>
          </div>
          <DualRangeSlider
            testId="combine-range"
            min={0}
            max={255}
            low={range.min}
            high={range.max}
            onChange={(min, max) => setRange({ min, max })}
          />
        </div>

        <Button
          data-testid="save-combined-layer"
          onClick={saveCombinedLayer}
          className="w-full brand-gradient text-white font-semibold"
        >
          <Save className="w-4 h-4 mr-1.5" /> {destination === "existing" ? "Update selected layer" : "Save as new layer"}
        </Button>
      </div>
    </div>
  );
};

const ChoiceButton = ({ active, children, onClick, testId }) => (
  <button
    data-testid={testId}
    type="button"
    onClick={onClick}
    className={`rounded-md border px-2 py-2 text-xs font-medium transition ${
      active
        ? "border-rose-500/50 bg-rose-500/15 text-rose-100"
        : "border-zinc-800 bg-zinc-900/50 text-zinc-400 hover:text-zinc-100"
    }`}
  >
    {children}
  </button>
);

const LayerSelect = ({ label, hint, layers, value, onChange, testId }) => (
  <label className="block space-y-2">
    <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
      {label}
      {hint && (
        <TooltipProvider delayDuration={80}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                data-testid={`${testId}-hint`}
                className="text-zinc-500 hover:text-rose-400 cursor-help transition normal-case"
              >
                <Info className="w-3 h-3" />
              </span>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              className="max-w-xs bg-[#141519] border-zinc-700 text-zinc-100"
            >
              <div className="text-xs leading-relaxed normal-case tracking-normal font-normal">
                {hint}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </span>
    <select
      data-testid={testId}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="h-10 w-full rounded-md border border-zinc-800 bg-[#131418] px-3 text-sm text-zinc-100 outline-none focus:border-rose-500/70"
    >
      {layers.map((layer) => (
        <option key={layer.id} value={layer.id}>
          {layer.name} — {layer.color.toUpperCase()}
        </option>
      ))}
    </select>
  </label>
);

const SliderRow = ({ label, value, min, max, step, onChange, format, testId }) => (
  <div className="space-y-1">
    <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
      <span>{label}</span>
      <span className="mono text-zinc-400 normal-case">{format ? format(value) : value}</span>
    </div>
    <input
      data-testid={testId}
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(event) => onChange(Number(event.target.value))}
      className="w-full accent-rose-500"
    />
  </div>
);
